import React from 'react'
import PropTypes from 'prop-types'
import { format, isValid, set, getYear, getMonth, getDate } from 'date-fns'
import { Flex, Button, Popover, PopoverContent, PopoverTrigger, useMultiStyleConfig, useControllableState, useDisclosure } from '@chakra-ui/react'
import { useDatepicker, START_DATE } from '@datepicker-react/hooks'

import { DatePickerCoreProvider, Month, Navigation, SelectedDate, NAVIGATION_GUIDE } from '../date-picker-core'

const today = new Date()
const defaultFormatAdapter = {
  firstDayOfWeek: 0,
  input: (date) => format(date, 'eee, MMMM d, yyyy'),
  month: (date) => format(date, 'MMMM, yyyy'),
  weekday: (date) => format(date, 'eee'),
  day: (date) => format(date, 'd'),
}

export const DatePicker = ({
  min,
  max,
  colorScheme,
  disabledDates,
  defaultValue,
  value: valueProp,
  onChange,
  isDisabled,
  formatAdapter: formatAdapterProp,
  todayText,
  placeholder,
  components,
  propsOf,
  ...rest
}) => {
  const baseStyle = useMultiStyleConfig('Popover', {}) // props can't be null or undefined
  const styleConfig = React.useMemo(() => ({ baseStyle: { ...baseStyle, popper: { ...baseStyle.popper, w: 'auto' } } }), [baseStyle])
  const formatAdapter = React.useMemo(() => ({ ...defaultFormatAdapter, ...formatAdapterProp }), [formatAdapterProp])

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [value, setValue] = useControllableState({ defaultValue, value: valueProp, onChange })

  const context = useDatepicker({
    startDate: value,
    endDate: value,
    minBookingDate: min,
    maxBookingDate: max,
    focusedInput: START_DATE,
    unavailableDates: disabledDates,
    firstDayOfWeek: formatAdapter.firstDayOfWeek,
    numberOfMonths: 1,
    onDatesChange: ({ startDate }) => {
      setValue(startDate)
      onClose()
    },
  })
  const onTodayClick = React.useCallback(
    () => context.onDateSelect(set(value, { year: getYear(today), month: getMonth(today), date: getDate(today) })),
    [value, context],
  )

  return (
    <Flex flex='1' position='relative' {...propsOf.container}>
      <DatePickerCoreProvider value={{ colorScheme, disabledDates, formatAdapter, ...context }}>
        <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement='bottom-start' styleConfig={styleConfig} {...propsOf.popover}>
          <PopoverTrigger {...propsOf.trigger}>
            {React.createElement(components.input, {
              flex: 1,
              isDisabled,
              ...rest,
              ...(isOpen ? { 'data-focus': isOpen } : {}),
              value: isValid(value) ? formatAdapter.input(value) : placeholder,
              readOnly: true,
            })}
          </PopoverTrigger>

          <PopoverContent padding='1.5rem' {...propsOf.content}>
            <Navigation guide={NAVIGATION_GUIDE.PREV} onClick={context.goToPreviousMonthsByOneMonth} {...propsOf.prevNavigation} />
            <Navigation
              guide={NAVIGATION_GUIDE.NEXT}
              onClick={context.goToNextMonthsByOneMonth}
              marginLeft='0.5rem'
              transform='translateX(100%)'
              {...propsOf.nextNavigation}
            />

            {context.activeMonths.map((active, index) => (
              <Month key={index} year={active.year} month={active.month} />
            ))}

            <Button position='absolute' top='0' right='0' margin='1.5rem' size='sm' variant='outline' onClick={onTodayClick} {...propsOf.todayButton}>
              {todayText}
            </Button>
          </PopoverContent>
        </Popover>
      </DatePickerCoreProvider>
    </Flex>
  )
}

DatePicker.propTypes = {
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  colorScheme: PropTypes.string,
  disabledDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  defaultValue: PropTypes.instanceOf(Date),
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  formatAdapter: PropTypes.shape({
    firstDayOfWeek: PropTypes.number,
    input: PropTypes.func,
    month: PropTypes.func,
    weekday: PropTypes.func,
    day: PropTypes.func,
  }),
  todayText: PropTypes.string,
  placeholder: PropTypes.string,
  components: PropTypes.shape({ input: PropTypes.elementType.isRequired }).isRequired,
  propsOf: PropTypes.shape({
    container: PropTypes.object,
    popover: PropTypes.object,
    trigger: PropTypes.object,
    content: PropTypes.object,
    prevNavigation: PropTypes.object,
    nextNavigation: PropTypes.object,
    todayButton: PropTypes.object,
  }).isRequired,
}

DatePicker.defaultProps = {
  colorScheme: 'brand',
  defaultValue: null,
  disabledDates: [],
  todayText: 'Today',
  placeholder: 'Select Date',
  components: { input: SelectedDate },
  propsOf: {},
}
